import React, { useEffect, useState } from 'react';
import mosqueService from '../../services/mosqueService';
import {
  DAILY_PRAYER_LABELS,
  DAILY_PRAYER_ORDER,
  boardHasTimings,
  emptyPrayerTimingsBoard,
  formatTimeCell,
  normalizePrayerTimingsBoard,
} from '../../utils/mosquePrayerTimings';

const inputClass =
  'w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500';

const TimingsTable = ({ rows, showNote }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 bg-gray-50 text-left text-gray-600">
          <th className="px-3 py-2 font-medium">Prayer</th>
          <th className="px-3 py-2 font-medium">Azaan</th>
          <th className="px-3 py-2 font-medium">Jamaat</th>
          {showNote && <th className="px-3 py-2 font-medium">Note</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className="border-b border-gray-100 last:border-0">
            <td className="px-3 py-2 font-medium text-gray-900">{row.label}</td>
            <td className="px-3 py-2 text-gray-800">{formatTimeCell(row.azaan)}</td>
            <td className="px-3 py-2 text-gray-800">{formatTimeCell(row.jamaat)}</td>
            {showNote && (
              <td className="px-3 py-2 text-gray-600">{formatTimeCell(row.note)}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MosquePrayerTimingsBoard = ({
  mosque,
  board: boardProp,
  canEdit = false,
  onSaved,
  className = '',
  embedded = false,
  value: embeddedValue,
  onChange: embeddedOnChange,
}) => {
  const initialBoard = normalizePrayerTimingsBoard(
    embedded ? embeddedValue : boardProp,
  );
  const [board, setBoard] = useState(() => initialBoard);
  const [draft, setDraft] = useState(() => initialBoard);
  const [editing, setEditing] = useState(embedded);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const next = normalizePrayerTimingsBoard(
      embedded ? embeddedValue : (boardProp ?? mosque?.prayer_timings_board),
    );
    if (embedded) {
      setDraft(next);
      return;
    }
    setBoard(next);
    if (!editing) {
      setDraft(next);
    }
  }, [boardProp, mosque?.prayer_timings_board, editing, embedded, embeddedValue]);

  const pushDraft = (updater) => {
    setDraft((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (embedded && embeddedOnChange) {
        embeddedOnChange(next);
      }
      return next;
    });
  };

  const mosqueKey = mosque?.slug || mosque?.id;

  const jummahReadRows = (board.jummah.length > 0
    ? board.jummah
    : [{ azaan: '', jamaat: '', note: '' }]
  ).map((row, index) => ({
    key: `jummah-${index}`,
    label: index === 0 ? 'Jummah' : `Jummah ${index + 1}`,
    azaan: row.azaan,
    jamaat: row.jamaat,
    note: row.note,
  }));

  const dailyReadRows = DAILY_PRAYER_ORDER.map((key) => ({
    key,
    label: DAILY_PRAYER_LABELS[key],
    azaan: board.daily[key]?.azaan,
    jamaat: board.daily[key]?.jamaat,
  }));

  const handleDailyChange = (prayerKey, field, value) => {
    pushDraft((prev) => ({
      ...prev,
      daily: {
        ...prev.daily,
        [prayerKey]: {
          ...prev.daily[prayerKey],
          [field]: value,
        },
      },
    }));
  };

  const handleJummahChange = (index, field, value) => {
    pushDraft((prev) => {
      const jummah = [...prev.jummah];
      while (jummah.length <= index) {
        jummah.push({ azaan: '', jamaat: '', note: '' });
      }
      jummah[index] = { ...jummah[index], [field]: value };
      return { ...prev, jummah };
    });
  };

  const addJummahRow = () => {
    pushDraft((prev) => ({
      ...prev,
      jummah: [...prev.jummah, { azaan: '', jamaat: '', note: '' }],
    }));
  };

  const removeJummahRow = (index) => {
    pushDraft((prev) => ({
      ...prev,
      jummah: prev.jummah.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!mosqueKey) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await mosqueService.updatePrayerTimings(mosqueKey, draft);
      const normalized = normalizePrayerTimingsBoard(saved);
      setBoard(normalized);
      setDraft(normalized);
      setEditing(false);
      onSaved?.(normalized);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.prayer_timings_board?.[0] ||
        'Failed to save prayer timings.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(board);
    setEditing(false);
    setError(null);
  };

  const hasContent = boardHasTimings(board);
  const editJummahRows = draft.jummah.length > 0 ? draft.jummah : [{ azaan: '', jamaat: '', note: '' }];
  const showEditForm = embedded || (editing && canEdit);
  const showReadView = !embedded && !editing;
  const wrapperClass = embedded
    ? className
    : `bg-white rounded-lg shadow-sm border p-6 mb-6 ${className}`.trim();

  return (
    <div className={wrapperClass || undefined}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className={`font-semibold text-gray-900 ${embedded ? 'text-lg' : 'text-xl'}`}>
          Prayer Timings
        </h2>
        {!embedded && canEdit && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="self-start bg-green-700 text-white px-4 py-2 rounded-md hover:bg-green-800 transition-colors text-sm"
          >
            Edit timings
          </button>
        )}
      </div>

      {error && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {showReadView && (
        <>
          {!hasContent ? (
            <p className="text-gray-600 text-sm">Prayer timings have not been published yet.</p>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-green-800 uppercase tracking-wide mb-2">
                  Jummah
                </h3>
                <TimingsTable rows={jummahReadRows.filter((r) => r.azaan || r.jamaat || r.note)} showNote />
                {jummahReadRows.filter((r) => r.azaan || r.jamaat || r.note).length === 0 && (
                  <p className="text-sm text-gray-500">No Jummah times listed.</p>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Daily prayers
                </h3>
                <TimingsTable rows={dailyReadRows} showNote={false} />
              </div>
            </div>
          )}
        </>
      )}

      {showEditForm && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            Use formats like 5:30, 12:45, or 1:30 PM. Jummah appears at the top of the board for visitors.
          </p>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-green-800 uppercase tracking-wide">Jummah</h3>
              <button
                type="button"
                onClick={addJummahRow}
                className="text-sm text-green-700 hover:text-green-900"
              >
                + Add Jummah slot
              </button>
            </div>
            <div className="space-y-3">
              {editJummahRows.map((row, index) => (
                <div
                  key={`edit-jummah-${index}`}
                  className="grid grid-cols-1 sm:grid-cols-4 gap-2 p-3 bg-green-50 border border-green-100 rounded-lg"
                >
                  <input
                    type="text"
                    aria-label={`Jummah ${index + 1} azaan`}
                    className={inputClass}
                    placeholder="Azaan"
                    value={row.azaan}
                    onChange={(e) => handleJummahChange(index, 'azaan', e.target.value)}
                  />
                  <input
                    type="text"
                    aria-label={`Jummah ${index + 1} jamaat`}
                    className={inputClass}
                    placeholder="Jamaat"
                    value={row.jamaat}
                    onChange={(e) => handleJummahChange(index, 'jamaat', e.target.value)}
                  />
                  <input
                    type="text"
                    aria-label={`Jummah ${index + 1} note`}
                    className={`${inputClass} sm:col-span-2`}
                    placeholder="Note (e.g. Khutbah hall)"
                    value={row.note}
                    onChange={(e) => handleJummahChange(index, 'note', e.target.value)}
                  />
                  {editJummahRows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeJummahRow(index)}
                      className="text-sm text-red-600 hover:text-red-800 sm:col-span-4 text-left"
                    >
                      Remove slot
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Daily prayers
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-gray-600">
                    <th className="px-3 py-2 font-medium">Prayer</th>
                    <th className="px-3 py-2 font-medium">Azaan</th>
                    <th className="px-3 py-2 font-medium">Jamaat</th>
                  </tr>
                </thead>
                <tbody>
                  {DAILY_PRAYER_ORDER.map((key) => (
                    <tr key={key} className="border-b border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-900">{DAILY_PRAYER_LABELS[key]}</td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          className={inputClass}
                          value={draft.daily[key]?.azaan ?? ''}
                          onChange={(e) => handleDailyChange(key, 'azaan', e.target.value)}
                          placeholder="Azaan"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          className={inputClass}
                          value={draft.daily[key]?.jamaat ?? ''}
                          onChange={(e) => handleDailyChange(key, 'jamaat', e.target.value)}
                          placeholder="Jamaat"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {!embedded && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="bg-green-700 text-white px-4 py-2 rounded-md hover:bg-green-800 disabled:opacity-50 text-sm"
              >
                {saving ? 'Saving...' : 'Save timings'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-50 text-sm"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MosquePrayerTimingsBoard;
